const REQUEST_TIMEOUT_MS = 20000;
const PRODUCT_SELECT_EVENT = 'shopify:product:select';

class ShippingCalculatorError extends Error {
  constructor(message, userMessage = 'Não foi possível calcular agora. Tente novamente.') {
    super(message);
    this.name = 'ShippingCalculatorError';
    this.userMessage = userMessage;
  }
}

class MaryssilShippingCalculator {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-shipping-form]');
    this.input = root.querySelector('[name="cep"]');
    this.submit = root.querySelector('[data-shipping-submit]');
    this.status = root.querySelector('[data-shipping-status]');
    this.results = root.querySelector('[data-shipping-results]');
    this.endpoint = root.dataset.shippingQuoteEndpoint || '';
    this.scope = root.closest('.product-details') || root.closest('.shopify-section') || document;
    this.eventScope = root.closest('.shopify-section') || document;
    this.abortController = null;
    this.pendingVariantSelection = null;
    this.runId = 0;
    this.busy = false;

    this.form?.addEventListener('submit', this.#handleSubmit);
    this.input?.addEventListener('input', this.#handleInput);
    this.eventScope.addEventListener(PRODUCT_SELECT_EVENT, this.#handleVariantSelect);
    this.scope.addEventListener('change', this.#handleProductChange);
  }

  #handleInput = () => {
    if (!(this.input instanceof HTMLInputElement)) return;
    const digits = this.#digits(this.input.value).slice(0, 8);
    const formatted = this.#formatCep(digits);
    if (this.input.value !== formatted) this.input.value = formatted;
    this.#invalidate();
  };

  #handleVariantSelect = (event) => {
    this.#invalidate();
    this.pendingVariantSelection = event?.promise && typeof event.promise.then === 'function'
      ? event.promise.catch(() => undefined)
      : null;
  };

  #handleProductChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('product-form-component')) return;
    this.#invalidate();
  };

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.busy) return;

    const digits = this.#digits(this.input?.value || '');
    if (digits.length !== 8) {
      this.#renderError('Digite um CEP válido com 8 números.');
      this.input?.focus();
      return;
    }

    if (!this.endpoint) {
      this.#renderError('O cálculo de frete ainda não está configurado neste ambiente.');
      return;
    }

    const selectionRunId = this.runId;
    await this.pendingVariantSelection;
    if (selectionRunId !== this.runId) return;
    this.pendingVariantSelection = null;

    const selection = this.#shippingSelection();
    if (selection.items.length === 0) {
      this.#renderError(selection.message);
      return;
    }

    void this.#calculate(this.#formatCep(digits), selection.items);
  };

  #invalidate() {
    this.runId += 1;
    this.abortController?.abort();
    this.abortController = null;
    this.busy = false;
    this.#setBusy(false);
    this.#clearResult();
  }

  #shippingSelection() {
    const productId = this.root.dataset.productId || '';
    const pairedPurchase = this.scope.querySelector('maryssil-paired-purchase');

    if (pairedPurchase) {
      const includedParts = Array.from(pairedPurchase.querySelectorAll('[data-pair-part]')).filter((part) => {
        const include = part.querySelector('[data-pair-include]');
        return include instanceof HTMLInputElement && include.checked;
      });

      const items = includedParts.map((part) => {
        const variantInput = part.querySelector('input[type="radio"][name]:checked');
        if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return null;
        const variant = this.#variantFor(part, variantInput.value);
        return variant ? this.#quoteItem(variant) : null;
      });

      if (includedParts.length === 0 || items.some((item) => !item || !item.variantId)) {
        return {
          items: [],
          message: 'Selecione o tamanho de cada peça antes de calcular o frete.',
        };
      }
      return { items };
    }

    const forms = Array.from(this.scope.querySelectorAll('product-form-component')).filter((form) => {
      if (productId && form.dataset.productId !== productId) return false;
      return !form.closest('quick-add-dialog');
    });

    const customSingleForm = forms.find((form) => {
      const variantInput = form.querySelector('maryssil-single-purchase')?.closest('product-form-component')?.querySelector('input[name="id"]');
      return variantInput instanceof HTMLInputElement && variantInput.value.trim() !== '';
    });
    const form = customSingleForm || [...forms].reverse().find((candidate) => {
      const variantInput = candidate.querySelector('input[name="id"]');
      return variantInput instanceof HTMLInputElement && variantInput.value.trim() !== '';
    });
    const variantInput = form?.querySelector('input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value.trim() : '';

    if (!variantId) {
      return {
        items: [],
        message: 'Selecione uma variante disponível antes de calcular o frete.',
      };
    }

    const variant = this.#variantFor(form, variantId) || this.#variantFor(this.scope, variantId);
    const quantityInput = form?.querySelector('input[ref="quantityInput"], input[name="quantity"]');
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    return variant && this.#quoteItem(variant).variantId
      ? { items: [{ ...this.#quoteItem(variant), quantity }] }
      : { items: [], message: 'Não foi possível identificar a variante selecionada.' };
  }

  #variantFor(container, variantId) {
    const data = container?.querySelector('[data-shipping-variants]')
      || container?.querySelector('[data-single-variants]');
    if (!(data instanceof HTMLScriptElement)) return null;

    try {
      const variants = JSON.parse(data.textContent || '[]');
      return Array.isArray(variants)
        ? variants.find((variant) => String(variant.id) === String(variantId)) || null
        : null;
    } catch (_) {
      return null;
    }
  }

  #quoteItem(variant) {
    const variantId = String(variant.id || '').trim();
    const priceCents = Number(variant.priceCents ?? variant.price);
    const weightGrams = Number(variant.weightGrams ?? variant.weight);
    return {
      variantId,
      sku: String(variant.sku || '').trim().slice(0, 80),
      valueCents: Number.isFinite(priceCents) && priceCents > 0
        ? Math.round(priceCents)
        : 0,
      weightGrams: Number.isFinite(weightGrams) && weightGrams > 0
        ? weightGrams
        : undefined,
      quantity: 1,
    };
  }

  async #calculate(zip, items) {
    const currentRunId = this.runId + 1;
    this.runId = currentRunId;
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    this.abortController = controller;
    this.busy = true;
    this.#clearResult();
    this.#setBusy(true);

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        cache: 'no-store',
        body: JSON.stringify({
          recipientZip: this.#digits(zip),
          items: items.map(({ variantId, quantity }) => ({
            variantId,
            quantity,
          })),
        }),
        signal: controller.signal,
      });
      const payload = await this.#readResponse(response);
      if (!response.ok) {
        throw new ShippingCalculatorError(
          `A cotação da Frenet falhou (${response.status}).`,
          payload?.error || 'Não foi possível calcular agora. Tente novamente.',
        );
      }

      if (currentRunId === this.runId) {
        this.#renderRates(payload?.rates, 'BRL', zip, payload?.address);
      }
    } catch (error) {
      if (currentRunId === this.runId && (timedOut || error?.name !== 'AbortError')) {
        this.#renderError(timedOut
          ? 'O cálculo demorou mais que o esperado. Tente novamente.'
          : error?.userMessage || 'Não foi possível calcular agora. Tente novamente.');
      }
    } finally {
      window.clearTimeout(timeoutId);
      if (this.abortController === controller) {
        this.abortController = null;
        this.busy = false;
        this.#setBusy(false);
      }
    }
  }

  #renderRates(rates, currency, zip, address) {
    this.#clearResult();
    if (!Array.isArray(rates) || rates.length === 0) {
      this.#renderError('Nenhuma modalidade da Frenet foi encontrada para este CEP.');
      return;
    }

    const table = document.createElement('table');
    const caption = document.createElement('caption');
    caption.textContent = `Opções de entrega para o CEP ${zip}`;
    table.append(caption);

    const thead = document.createElement('thead');
    const headingRow = document.createElement('tr');
    for (const label of ['Modalidade', 'Prazo', 'Preço']) {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label;
      headingRow.append(cell);
    }
    thead.append(headingRow);
    table.append(thead);

    const tbody = document.createElement('tbody');
    for (const rate of rates) {
      const row = document.createElement('tr');
      const nameCell = document.createElement('th');
      nameCell.scope = 'row';
      const name = document.createElement('span');
      name.className = 'maryssil-product-assurance__shipping-rate-name';
      name.textContent = this.#displayRateName(rate);
      nameCell.append(name);
      row.append(nameCell);

      const deliveryCell = document.createElement('td');
      deliveryCell.textContent = this.#deliveryDetail(rate) || 'Consulte no checkout';
      row.append(deliveryCell);

      const priceCell = document.createElement('td');
      priceCell.className = 'maryssil-product-assurance__shipping-rate-price';
      priceCell.textContent = this.#formatPrice(rate?.price, currency);
      row.append(priceCell);
      tbody.append(row);
    }
    table.append(tbody);
    this.results?.append(table);
    this.#appendAddressHelp(address, zip);
    if (this.results) this.results.hidden = false;
    if (this.status) {
      this.status.textContent = `Encontramos ${rates.length === 1 ? 'uma opção' : `${rates.length} opções`} da Frenet. A tarifa é consultada no mesmo cálculo do checkout.`;
    }
  }

  #appendAddressHelp(address, zip) {
    if (!this.results || !address || typeof address !== 'object') return;

    const addressLine = [address.address1, address.address2]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(', ');
    const locality = [address.city, address.province]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');
    if (!addressLine && !locality) return;

    const note = document.createElement('p');
    note.className = 'maryssil-product-assurance__shipping-address-help';
    const title = document.createElement('strong');
    title.textContent = 'Endereço para conferir no checkout: ';
    note.append(title);
    note.append(document.createTextNode([addressLine, locality].filter(Boolean).join(' — ')));
    note.append(document.createTextNode(`. Se a Shopify não mostrar sugestão para o CEP ${zip}, preencha esses campos manualmente e informe o número do imóvel.`));
    this.results.append(note);
  }

  #displayRateName(rate) {
    const name = String(rate?.presentment_name || rate?.name || 'Entrega').trim();
    const normalized = name.toLowerCase();
    return normalized.includes('local delivery')
      || normalized.includes('entrega local')
      || normalized.includes('motoboy')
      || normalized.includes('retirada na loja')
      ? 'Entrega local'
      : name;
  }

  #deliveryDetail(rate) {
    const days = Array.isArray(rate?.delivery_days)
      ? rate.delivery_days.map(Number).filter(Number.isFinite)
      : [];
    if (days.length > 0) {
      const minimum = Math.min(...days);
      const maximum = Math.max(...days);
      if (minimum === maximum) return `Em ${maximum} dias úteis`;
      if (minimum === 0) return `Até ${maximum} dias úteis`;
      return `${minimum} a ${maximum} dias úteis`;
    }

    if (rate?.delivery_date) {
      const deliveryDate = new Date(`${rate.delivery_date}T00:00:00Z`);
      if (!Number.isNaN(deliveryDate.getTime())) {
        return `Até ${new Intl.DateTimeFormat('pt-BR').format(deliveryDate)}`;
      }
    }
    return '';
  }

  #formatPrice(value, currency) {
    const amount = Number.parseFloat(String(value ?? '').replace(',', '.'));
    if (!Number.isFinite(amount)) return 'Consulte no checkout';
    const code = /^[A-Za-z]{3}$/.test(String(currency)) ? String(currency).toUpperCase() : 'BRL';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(amount);
  }

  #renderError(message) {
    this.#clearResult();
    if (this.status) this.status.textContent = message;
  }

  #clearResult() {
    if (this.results) {
      this.results.replaceChildren();
      this.results.hidden = true;
    }
    if (this.status) this.status.textContent = '';
  }

  #setBusy(isBusy) {
    this.root.toggleAttribute('aria-busy', isBusy);
    if (this.submit instanceof HTMLButtonElement) this.submit.disabled = isBusy;
    if (isBusy && this.status) this.status.textContent = 'Calculando frete...';
  }

  async #readResponse(response) {
    const payload = await response.text();
    try {
      return JSON.parse(payload);
    } catch (_) {
      return {};
    }
  }

  #digits(value) {
    return String(value).replace(/\D/g, '');
  }

  #formatCep(digits) {
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
  }
}

function mountShippingCalculators(container = document) {
  const roots = container.matches?.('[data-maryssil-shipping-calculator]')
    ? [container]
    : Array.from(container.querySelectorAll?.('[data-maryssil-shipping-calculator]') || []);

  for (const root of roots) {
    if (root.dataset.shippingCalculatorReady === 'true') continue;
    root.dataset.shippingCalculatorReady = 'true';
    new MaryssilShippingCalculator(root);
  }
}

mountShippingCalculators();
document.addEventListener('shopify:section:load', (event) => mountShippingCalculators(event.target));
